import { z } from 'zod';

export type CEFRLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

export const CEFR_LEVELS: CEFRLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

export type Language = 'English' | 'Spanish' | 'French' | 'German' | 'Italiano' | 'Norwegian';

export const LANGUAGES: Language[] = ['English', 'Spanish', 'French', 'German', 'Italiano', 'Norwegian'];

export type BookGenre = 
  | 'Romance'
  | 'Mystery'
  | 'Thriller'
  | 'Fantasy'
  | 'Fiction'
  | 'Science Fiction'
  | 'Adventure'
  | 'Young Adult'
  | 'Nonfiction'
  | 'Historical Fiction'
  | 'Horror'
  | "Children's Books"
  | 'Biography'
  | 'Self-Help';

export const BOOK_GENRES: BookGenre[] = [
  'Romance',
  'Mystery',
  'Thriller',
  'Fantasy',
  'Fiction',
  'Science Fiction',
  'Adventure',
  'Young Adult',
  'Nonfiction',
  'Historical Fiction',
  'Horror',
  "Children's Books"
];

export interface Book {
  id: string;
  title: string;
  author?: string;
  status: 'Processing' | 'Completed' | 'Failed';
  processedContent?: string;
  storagePath?: string;
  userId?: string;
  targetLanguage?: string;
  complexityLevel?: string;
  createdAt?: any;
  completedAt?: any;
  failedAt?: any;
  progress?: number;
  error?: string;
  aiProvider?: string;
  format?: string; // 'fb2', 'epub2', 'epub3'
}

export interface User {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  createdAt?: string | null;
  role?: 'user' | 'admin';
  preferred_study_language?: string;
  preferred_interface_language?: string;
  preferred_cefr_level?: string;
  user_type?: 'learner' | 'translator' | 'reader';
  native_language?: string;
  onboarding_completed?: boolean;
}

export interface LibraryBookTranslation {
  id: string;
  cefr_level: string | null;
  is_human_translation: boolean;
  file_format: string;
  status: string; // 'available', 'processing', 'failed', etc.
}

export interface LibraryBook {
  id: string;
  title_original?: string; // Optional: not returned in list views (popular/books), only in detail view
  title_international: string;
  author_original?: string; // Optional: not returned in list views (popular/books), only in detail view
  author_international: string;
  cover_url?: string | null; // URL to cover.webp file (e.g., /covers/library/{book_id}/cover.webp)
  description?: string | null; // Optional: not returned in list views (popular/books), only in detail view
  original_file_path?: string; // Optional: only in detail view
  original_language?: string;
  available_languages: string[];
  translations?: Record<string, LibraryBookTranslation[]>; // Optional: not returned in list views (popular/books), only in detail view
  format?: string; // 'fb2', 'epub2', 'epub3'
  genre?: BookGenre | null;
  estimated_pages?: number | null; // Estimated page count (~250 words/page)
}

export interface APIKeyStatus {
  configured: boolean;
  name: string;
}

export interface AIProvider {
  name: string;
  model: string;
}

export interface UserPreferences {
  preferred_study_language?: string;
  preferred_interface_language?: string;
  preferred_cefr_level?: string;
  preferred_translation_model?: string;
  user_type?: 'learner' | 'translator' | 'reader';
  native_language?: string;
  onboarding_completed?: boolean;
  api_keys?: Record<string, APIKeyStatus>;
}

export interface OnboardingState {
  userType: 'learner' | 'translator' | 'reader' | null;
  nativeLanguage: string | null;
  targetLanguage: string | null;
  cefrLevel: CEFRLevel | null;
  completed: boolean;
}

const CEFRLevels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'] as const;

export const TranslateAndSimplifyBookInputSchema = z.object({
  bookContent: z.string().describe('The content of the book to be translated and simplified.'),
  targetLanguage: z.string().describe('The target language for translation (e.g., English, Spanish, French).'),
  complexityLevel: z.enum(CEFRLevels).describe('The desired complexity level (A1, A2, B1, B2, C1, C2).'),
  title: z.string().describe('The title of the book.'),
  userId: z.string().describe('The user ID.'),
  bookId: z.string().describe('The unique ID for the book.'),
});
export type TranslateAndSimplifyBookInput = z.infer<typeof TranslateAndSimplifyBookInputSchema>;

export const TranslateAndSimplifyBookOutputSchema = z.object({
  simplifiedContent: z.string().describe('The translated and simplified content of the book.'),
});
export type TranslateAndSimplifyBookOutput = z.infer<typeof TranslateAndSimplifyBookOutputSchema>;

// Membership types
export type MembershipTier = 'basic' | 'plus' | 'book_giant';

export interface MembershipTierConfig {
  tier_id: MembershipTier;
  name: string;
  price_monthly_usd: number;
  free_downloads_per_month: number;
  translation_discount_percent: number;
  description: string;
}

export interface UserMembership {
  id: number;
  user_id: string;
  tier: MembershipTier;
  tier_name?: string;
  tier_price?: number;
  tier_description?: string;
  free_downloads_per_month: number;
  translation_discount_percent: number;
  credit_balance_usd: number;  // Prepaid credit balance
  tokens_used_this_month: number;
  downloads_this_month: number;
  billing_cycle_start: string;
  billing_cycle_end: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MembershipLimits {
  downloads: {
    limit: number;
    used: number;
    remaining: number;
    can_download: boolean;
  };
  discount_percent: number;
  billing_cycle_start: string | null;
  billing_cycle_end: string | null;
}

// Credit pack types
export interface CreditPack {
  pack_id: string;
  name: string;
  price_usd: number;
  bonus_percent: number;
  credit_amount_usd: number;
  description: string;
  popular: boolean;
}

export interface CreditPurchase {
  id: string;
  user_id: string;
  pack_size_usd: number;
  bonus_percent: number;
  credit_amount_usd: number;
  status: 'pending' | 'completed' | 'failed';
  payment_provider?: string | null;
  payment_provider_transaction_id?: string | null;
  description?: string | null;
  created_at: string;
  updated_at: string;
}

// Transaction types
export type TransactionType = 'translation' | 'subscription' | 'download' | 'refund' | 'credit_purchase' | 'credit_purchase_refund' | 'reward';
export type TransactionStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export interface Transaction {
  id: string;
  user_id: string;
  type: TransactionType;
  status: TransactionStatus;
  amount: number;
  currency: string;
  translation_id?: string | null;
  tokens_used?: number | null;
  free_tokens_applied?: number | null;
  membership_tier?: MembershipTier | null;
  payment_provider?: string | null;
  payment_provider_transaction_id?: string | null;
  description?: string | null;
  extra_data?: string | null;
  created_at: string;
  updated_at: string;
}

// Reward types
export type RewardType = 'credits' | 'bonus_downloads';

export interface RewardDefinition {
  id: number;
  code: string;
  name: string;
  description: string;
  credit_amount_usd: number;
  reward_type: RewardType;
  bonus_download_amount: number | null;
  is_active: boolean;
  auto_assign_on_registration: boolean;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

export type UserRewardStatus = 'pending' | 'claimed' | 'expired';

export interface UserReward {
  id: number;
  user_id: string;
  reward_id: number;
  status: UserRewardStatus;
  assigned_at: string;
  claimed_at: string | null;
  expires_at: string | null;
  source_referral_id: number | null;
  reward?: RewardDefinition;
}

// Referral types
export type ReferralStatus = 'signed_up' | 'active';

export interface ReferredFriend {
  id: number;
  referrer_user_id: string;
  referred_user_id: string;
  referral_code_used: string;
  status: ReferralStatus;
  signed_up_at: string;
  became_active_at: string | null;
  created_at: string;
  referred_email_masked?: string;
  referred_name?: string;
}

export interface ReferralStats {
  referral_code: string | null;
  total_referrals: number;
  active_referrals: number;
  pending_referrals: number;
  referrals: ReferredFriend[];
  lifetime_stats: {
    total_downloads_earned: number;
    total_credits_earned: number;
  };
}

export interface ReferralLink {
  code: string;
  link: string;
}

export type GoalType = 'referral_count' | 'translation_count' | 'download_count' | 'custom';
export type GoalRewardType = 'credits' | 'bonus_downloads' | 'badge';
export type GoalStatus = 'in_progress' | 'completed' | 'claimed';

export interface ReferralGoal {
  id: number;
  code: string;
  name: string;
  description: string;
  goal_type: GoalType;
  required_count: number;
  reward_type: GoalRewardType;
  reward_amount: number;
  badge_code: string | null;
  is_active: boolean;
  sort_order: number;
  // Progress info (populated per-user)
  current_count: number;
  progress_percent: number;
  status: GoalStatus;
  user_goal_id: number | null;
  completed_at: string | null;
  claimed_at: string | null;
}

export interface BonusDownloadEntry {
  id: number;
  user_id: string;
  amount: number;
  remaining: number;
  source_type: string;
  source_referral_id: number | null;
  source_goal_id: number | null;
  expires_at: string;
  created_at: string;
}

export interface BonusDownloadsSummary {
  total_available: number;
  by_source: Record<string, number>;
  earliest_expiry: string | null;
  entries: BonusDownloadEntry[];
}

// Translation cost estimation types (simplified - no tokens/model shown to user)
export interface TranslationCostEstimate {
  estimate_id: string;
  estimated_characters: number;
  // Cost breakdown for UI display
  base_cost: number;              // Cost before membership discount (USD)
  discount_percent: number;       // Membership discount percentage (0, 25, or 50)
  discount_amount: number;        // Discount amount in USD
  final_cost: number;             // Final cost after discount (USD) - this is what user pays
  // Balance info
  credit_balance: number;         // User's current credit balance
  credits_needed: number;         // Additional credits needed (0 if balance sufficient)
  needs_credit_purchase: boolean; // True if user needs to buy more credits
  recommended_pack?: CreditPack | null;
  membership_tier: MembershipTier;
}
